import PropTypes from 'prop-types';
import React, { Component } from 'react';
import injectSheet from 'react-jss';

import { RIGHT } from '~/common/constants/keyCodes';
import EcFilterInput from '~/components/Shared/EcFilterInput/EcFilterInput';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { testHasTextMatch } from '~/eds/src/utils/testHasTextMatch';
import { injectIntl } from 'react-intl';

import EcModalCard from '../../Shared/EcModalCard/EcModalCard';
import ChevronRightIcon from '../../Shared/Icons/ChevronRightIcon';
import styles from './AddFilterModal.styles';
import { getPinnedFilters } from './AddFilterModal.utils';

class AddFilterModal extends Component {
  constructor(props) {
    super(props);

    this.handleOnCategoryClick = this.handleOnCategoryClick.bind(this);
    this.handleOnSearchChange = this.handleOnSearchChange.bind(this);

    this.state = { query: '', activeCategoryIndex: 0 };

    this.searchWordsContainOptions = [
      {
        value: 'contains',
        label: props.intl.formatMessage({
          id: 'AddFilterModal.containsOption',
          defaultMessage: 'contains',
          description: 'Search contains option label',
        }),
      },
      {
        value: 'does_not_contain',
        label: props.intl.formatMessage({
          id: 'AddFilterModal.doesNotContainOption',
          defaultMessage: 'does not contain',
          description: 'Search does not contain option label',
        }),
      },
    ];
  }

  handleOnSearchChange(query) {
    this.setState({ query, activeCategoryIndex: 0 });
  }

  handleOnPinClick(filterId) {
    this.props.handleFilterOptionsChange(filterId);
  }

  handleOnCategoryClick(activeCategoryIndex) {
    this.setState({ activeCategoryIndex });
  }

  handleOnCategoryKepress(e, activeCategoryIndex) {
    if (e.keyCode === RIGHT) {
      this.setState({ activeCategoryIndex });
    }
  }

  handleOnFilterClick(filter) {
    const { handleAddFilter, hideModal, replacingFilterIndex } = this.props;

    handleAddFilter(filter, replacingFilterIndex);
    hideModal();
  }

  handleOnProvisionClick(provisionName) {
    const { handleAddProvision, hideModal, replacingFilterIndex } = this.props;

    const provision = {
      entity: 'provision',
      provision: provisionName,
      contains: this.searchWordsContainOptions[0],
      text_search: [],
    };

    handleAddProvision(provision, replacingFilterIndex);
    hideModal();
  }

  handleOnTextSearchClick(textSearch) {
    const { handleAddTextSearch, hideModal, replacingFilterIndex, intl } =
      this.props;

    const searchWordsScopeOptions = [
      {
        value: 'all_of_these_words',
        label: intl.formatMessage({
          id: 'AddFilterModal.allOfTheseWordsOption',
          defaultMessage: 'all of these words',
          description: 'Search scope all words option label',
        }),
      },
      {
        value: 'any_of_these_words',
        label: intl.formatMessage({
          id: 'AddFilterModal.anyOfTheseWordsOption',
          defaultMessage: 'any of these words',
          description: 'Search scope any words option label',
        }),
      },
      {
        value: 'exact_phrase',
        label: intl.formatMessage({
          id: 'AddFilterModal.exactPhraseOption',
          defaultMessage: 'exact phrase',
          description: 'Search scope exact phrase option label',
        }),
      },
    ];

    const textSearchObject = {
      entity: 'text_search',
      area: textSearch,
      contains: this.searchWordsContainOptions[0],
      scope: searchWordsScopeOptions[0],
      text: '',
    };

    handleAddTextSearch(textSearchObject, replacingFilterIndex);
    hideModal();
  }

  handleOnBooleanSearchClick() {
    const { handleAddBooleanTextSearch, hideModal, replacingFilterIndex } =
      this.props;

    const boolTextSearchObject = {
      entity: 'bool_text_search',
      query: '',
    };

    handleAddBooleanTextSearch(boolTextSearchObject, replacingFilterIndex);
    hideModal();
  }

  getFilteredCategories(categories) {
    const { query } = this.state;

    if (!query) return categories;

    return categories.reduce((arr, category) => {
      const [categoryFields, fieldsType] = category.provisions
        ? [category.provisions, 'provisions']
        : category.textSearches
          ? [category.textSearches, 'textSearches']
          : [category.fields, 'fields'];

      const matchingFields = categoryFields.filter((f) => {
        const categoryName = f.hasOwnProperty('label') ? f.label : f;
        return testHasTextMatch(categoryName, query);
      });

      if (matchingFields.length) {
        return [...arr, { ...category, [fieldsType]: matchingFields }];
      }

      return arr;
    }, []);
  }

  renderFilterList(categories) {
    const { classes, enablePinning, intl } = this.props;
    const { activeCategoryIndex } = this.state;

    const activeCategory = categories[activeCategoryIndex];

    if (!activeCategory) {
      return <div className={classes.filterList} />;
    }

    if (activeCategory.textSearches) {
      return (
        <div className={classes.filterList}>
          {activeCategory.textSearches.map((textSearch, index) => (
            <button
              className={classes.filterListItem}
              key={`text-search-${index}`}
              onClick={() => this.handleOnTextSearchClick(textSearch)}
            >
              {textSearch.label}
            </button>
          ))}
        </div>
      );
    }

    if (activeCategory.provisions) {
      return (
        <div className={classes.filterList}>
          {activeCategory.provisions.map((provisionName, index) => (
            <button
              className={classes.filterListItem}
              key={`provision-${index}`}
              onClick={() => this.handleOnProvisionClick(provisionName)}
            >
              {provisionName}
            </button>
          ))}
        </div>
      );
    }

    return (
      <div className={classes.filterList}>
        {activeCategory.fields.map((filter) => {
          const dataForValue = `pin-icon_${filter.id}`;

          return (
            <div className={classes.filterListItem} key={filter.id}>
              <button
                className={classes.filterListButton}
                onClick={() => this.handleOnFilterClick(filter)}
              >
                {filter.label}
              </button>
              {enablePinning && (
                <IconButton
                  id={dataForValue}
                  tooltip={
                    filter.is_pinned
                      ? intl.formatMessage({
                          id: 'AddFilterModal.unpinTooltip',
                          defaultMessage: 'Unpin',
                          description: 'Unpin button tooltip',
                        })
                      : intl.formatMessage({
                          id: 'AddFilterModal.pinTooltip',
                          defaultMessage: 'Pin',
                          description: 'Pin button tooltip',
                        })
                  }
                  text={intl.formatMessage(
                    {
                      id: 'AddFilterModal.pinButtonText',
                      defaultMessage: 'pin {filterLabel}',
                      description:
                        'Pin button accessibility text. Example: filterLabel = "Status"',
                    },
                    { filterLabel: filter.label },
                  )}
                  onClick={() => this.handleOnPinClick(filter.id)}
                  icon={filter.is_pinned ? 'push-pin-filled' : 'push-pin'}
                  size="s"
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  renderFilterCategories(filterCategories) {
    const { classes, intl } = this.props;
    const { activeCategoryIndex } = this.state;

    return (
      <div className={classes.filterCategories}>
        {filterCategories.map((filterCategory, categoryIndex) => {
          const filterCategoryClass =
            activeCategoryIndex === categoryIndex
              ? classes.activeFilterCategory
              : classes.filterCategory;

          if (
            filterCategory.title ===
            intl.formatMessage({
              id: 'AddFilterModal.filterPinnedCategoryTitle',
              defaultMessage: 'Boolean Text Search',
              description: 'Boolean text search category title ',
            })
          ) {
            return (
              <button
                className={filterCategoryClass}
                onClick={() => this.handleOnBooleanSearchClick()}
                key={filterCategory.title}
              >
                <span>{filterCategory.title}</span>
              </button>
            );
          }

          return (
            <button
              aria-expanded={activeCategoryIndex === categoryIndex}
              className={filterCategoryClass}
              onClick={() => this.handleOnCategoryClick(categoryIndex)}
              onKeyDown={(e) => this.handleOnCategoryKepress(e, categoryIndex)}
              key={filterCategory.title}
            >
              <span>{filterCategory.title}</span>
              <ChevronRightIcon size="20" opacity="0.5" />
            </button>
          );
        })}
      </div>
    );
  }

  render() {
    const {
      classes,
      enablePinning,
      enableTextSearch,
      filterOptions,
      hideModal,
      provisionOptions,
      intl,
    } = this.props;
    const { query } = this.state;

    const searchAreaOptions = [
      {
        value: 'filename_content',
        label: intl.formatMessage({
          id: 'AddFilterModal.filenameAndContentOption',
          defaultMessage: 'Filename and Content',
          description: 'Search area option for filename and content',
        }),
      },
      {
        value: 'filename',
        label: intl.formatMessage({
          id: 'AddFilterModal.filenameOption',
          defaultMessage: 'Filename',
          description: 'Search area option for filename only',
        }),
      },
      {
        value: 'content',
        label: intl.formatMessage({
          id: 'AddFilterModal.contentOption',
          defaultMessage: 'Content',
          description: 'Search area option for content only',
        }),
      },
    ];

    let totalFilterCategories = [];
    if (enablePinning) {
      totalFilterCategories.push({
        title: intl.formatMessage({
          id: 'AddFilterModal.pinnedCategoryTitle',
          defaultMessage: 'Pinned',
          description: 'Pinned filters category title',
        }),
        fields: getPinnedFilters(filterOptions),
      });
    }
    totalFilterCategories = [...totalFilterCategories, ...filterOptions];
    if (provisionOptions.length) {
      totalFilterCategories.push({
        title: intl.formatMessage({
          id: 'AddFilterModal.clausesCategoryTitle',
          defaultMessage: 'Clauses',
          description: 'Clauses category title',
        }),
        provisions: provisionOptions,
      });
    }
    if (enableTextSearch) {
      totalFilterCategories.push({
        title: intl.formatMessage({
          id: 'AddFilterModal.textSearchCategoryTitle',
          defaultMessage: 'Text Search',
          description: 'Text search category title',
        }),
        textSearches: searchAreaOptions,
      });
      totalFilterCategories.push({
        title: intl.formatMessage({
          id: 'AddFilterModal.booleanTextSearchTitle',
          defaultMessage: 'Boolean Text Search',
          description:
            'Boolean text search category title for total filter categories',
        }),
        fields: [],
      });
    }

    const filteredCategories = this.getFilteredCategories(
      totalFilterCategories,
    );

    return (
      <EcModalCard
        title={intl.formatMessage({
          id: 'AddFilterModal.modalTitle',
          defaultMessage: 'Add a Filter',
          description: 'Modal title for adding a filter',
        })}
        content={
          <div className={classes.modalContent}>
            <div className={classes.filterCategoriesWrapper}>
              <div className={classes.filterSearchWrapper}>
                <EcFilterInput
                  value={query}
                  onChange={(query) => this.handleOnSearchChange(query)}
                  onClearSearch={() => this.handleOnSearchChange('')}
                  placeholder={intl.formatMessage({
                    id: 'AddFilterModal.searchPlaceholder',
                    defaultMessage: 'Search for filters...',
                    description: 'Placeholder text for filter search input',
                  })}
                />
              </div>
              {this.renderFilterCategories(filteredCategories)}
            </div>
            {this.renderFilterList(filteredCategories)}
          </div>
        }
        hideModal={hideModal}
      />
    );
  }
}

AddFilterModal.propTypes = {
  enablePinning: PropTypes.bool,
  enableTextSearch: PropTypes.bool,
  filterOptions: PropTypes.array.isRequired,
  provisionOptions: PropTypes.array.isRequired,
  handleFilterOptionsChange: PropTypes.func.isRequired,
  handleAddFilter: PropTypes.func.isRequired,
  handleAddProvision: PropTypes.func.isRequired,
  handleAddTextSearch: PropTypes.func.isRequired,
  handleAddBooleanTextSearch: PropTypes.func.isRequired,
  replacingFilterIndex: PropTypes.number,
  intl: PropTypes.object.isRequired,
};

AddFilterModal.defaultProps = {
  enablePinning: true,
  enableTextSearch: true,
};

export default injectSheet(styles)(injectIntl(AddFilterModal));
